import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PanelHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Plus, Sparkles, Phone, Mail, ArrowRight, X, Pencil, RefreshCw } from "lucide-react";
import { STAGES, NEW_LEAD_STAGES, RENEWAL_STAGES, STAGE_META, STAGE_PROBABILITY, SOURCE_LABEL, INR } from "@/lib/constants";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

// Convert "HH:MM" 24h → "h:mm AM/PM" (e.g. "06:00" → "6:00 AM", "19:30" → "7:30 PM")
function fmt12hLead(hhmm) {
  if (!hhmm || typeof hhmm !== "string" || !hhmm.includes(":")) return hhmm || "";
  const [hStr, mStr] = hhmm.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const ampm = h < 12 ? "AM" : "PM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

// Compact program label — prefer the code, fall back to raw string.
const PROGRAM_LABEL = {
  STRENGTH: "Strength",
  CONDITIONING: "Conditioning",
  MMA: "MMA",
  HYROX: "Hyrox",
  OPEN_GYM: "Open Gym",
};
function programChipLabel(l) {
  const raw = l.programme_code || l.programme_label || l.program_interest;
  if (!raw) return null;
  return PROGRAM_LABEL[raw] || raw;
}

// Heat calc — how fresh is this pipeline entry? Falls back to created_at
// when updated_at is missing (never-touched lead).
// Buckets tuned for a sales pipeline where 2 weeks = definitely cold.
function calcHeat(l) {
  const ts = l.updated_at || l.created_at;
  if (!ts) return { level: "warm", color: "#f59e0b", label: "Warm" };
  const ageMs = Date.now() - new Date(ts).getTime();
  const days = ageMs / 86400000;
  if (days <= 2) return { level: "hot",  color: "#22c55e", label: "Hot · <2d" };
  if (days <= 6) return { level: "warm", color: "#eab308", label: "Warm · <1w" };
  if (days <= 13) return { level: "cool", color: "#f97316", label: "Cooling · <2w" };
  return { level: "cold", color: "#ef4444", label: `Cold · ${Math.floor(days)}d idle` };
}

// Human-friendly renewal date: "12 Sep" if within a year, else "12 Sep '27".
function fmtRenewalDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const mo = d.toLocaleString("en-US", { month: "short" });
  const day = d.getDate();
  const sameYear = d.getFullYear() === now.getFullYear();
  return sameYear ? `${day} ${mo}` : `${day} ${mo} '${String(d.getFullYear()).slice(-2)}`;
}

export default function SalesCRMPage() {
  const { activeBranchId, user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [openNew, setOpenNew] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [refreshingRenewals, setRefreshingRenewals] = useState(false);

  const params = activeBranchId ? { branch_id: activeBranchId } : {};

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ["leads", activeBranchId],
    queryFn: async () => (await apiClient.get("/leads", { params })).data,
  });
  const { data: branches = [] } = useQuery({
    queryKey: ["branches"],
    queryFn: async () => (await apiClient.get("/branches")).data,
  });
  const { data: coaches = [] } = useQuery({
    queryKey: ["coaches-list"],
    queryFn: async () => (await apiClient.get("/users", { params: { role: "COACH" } })).data,
  });
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await apiClient.get("/settings")).data,
  });

  const updateSettings = useMutation({
    mutationFn: async (body) => (await apiClient.patch("/settings", body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Renewal window updated");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const refreshRenewals = async () => {
    setRefreshingRenewals(true);
    try {
      const { data } = await apiClient.post("/renewals/materialize");
      toast.success(`Renewals synced · ${data.created} added, ${data.auto_closed} closed`);
      qc.invalidateQueries({ queryKey: ["leads"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Renewal sync failed");
    } finally {
      setRefreshingRenewals(false);
    }
  };

  const updateLead = useMutation({
    mutationFn: async ({ id, body }) => (await apiClient.patch(`/leads/${id}`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leads"] }),
  });

  const createLead = useMutation({
    mutationFn: async (body) => (await apiClient.post("/leads", body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leads"] });
      toast.success("Lead added");
      setOpenNew(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const onDragStart = (e, leadId) => {
    e.dataTransfer.setData("text/lead", leadId);
  };
  const onDrop = (e, stage) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/lead");
    if (!id) return;
    const lead = leads.find((l) => l.id === id);
    if (!lead || lead.stage === stage) return;
    updateLead.mutate({ id, body: { stage } }, {
      onSuccess: () => toast.success(`Moved to ${STAGE_META[stage].label}`),
      onError: (err) => toast.error(err?.response?.data?.detail || "Move failed"),
    });
  };

  // Pipeline filter — three-way segmented control (ALL / LEAD / RENEWAL).
  // Purely client-side; the server still returns everything.
  const [pipelineFilter, setPipelineFilter] = useState("ALL");

  // The renewal-window dropdown is a VIEW filter only — hide any RENEWAL card
  // whose expiry sits beyond today+window. We deliberately restrict the filter
  // to NEW-stage cards so cards the team has already started working on
  // (CONTACTED / CONSULTATION / TRIAL / etc.) never disappear when someone
  // narrows the window. Terminal stages (WON/LOST/DROPPED_OUT) are unaffected.
  const windowDays = Number(settings?.renewal_window_days || 30);
  const horizonIso = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + windowDays);
    return d.toISOString().slice(0, 10);
  }, [windowDays]);
  const visibleLeads = useMemo(
    () => leads.filter((l) => {
      // 1. Pipeline filter (All / New Leads / Renewals)
      const kind = l.kind || "LEAD";                    // legacy leads → treat as LEAD
      if (pipelineFilter === "LEAD" && kind !== "LEAD") return false;
      if (pipelineFilter === "RENEWAL" && kind !== "RENEWAL") return false;
      // 2. Renewal-window view filter (renewals only, NEW stage only)
      if (l.kind !== "RENEWAL") return true;
      if (l.stage !== "NEW") return true;               // keep worked cards visible
      if (!l.renewal_expiry_date) return true;
      return l.renewal_expiry_date <= horizonIso;       // hide out-of-window renewals
    }),
    [leads, horizonIso, pipelineFilter],
  );

  // Weighted-pipeline + WON MTD — computed off `visibleLeads` so they honour
  // the branch filter and any active pipeline/window filters. Cheap
  // reduce-in-render; no memo needed for a list this size.
  const monthStartIso = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
  const weighted = (l) => (Number(l.budget) || 0) * (STAGE_PROBABILITY[l.stage] ?? 0);
  const newLeadPipeline = visibleLeads
    .filter((l) => (l.kind || "LEAD") === "LEAD")
    .reduce((s, l) => s + weighted(l), 0);
  const renewalPipeline = visibleLeads
    .filter((l) => l.kind === "RENEWAL")
    .reduce((s, l) => s + weighted(l), 0);
  const wonMTD = visibleLeads
    .filter((l) => l.stage === "WON" && (l.updated_at || "") >= monthStartIso)
    .reduce((s, l) => s + (Number(l.won_revenue) || 0), 0);

  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="sales-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold">Sales CRM</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">Sales Pipeline</h1>
          <div className="text-zinc-500 text-sm mt-1" data-testid="pipeline-subtitle">
            {visibleLeads.filter((l) => l.kind !== "RENEWAL").length} new leads
            <span className="text-zinc-400"> · {INR(newLeadPipeline)} weighted</span>
            {"  |  "}
            {visibleLeads.filter((l) => l.kind === "RENEWAL").length} renewals due
            <span className="text-zinc-400"> · {INR(renewalPipeline)} weighted</span>
            {"  |  "}
            <span className="text-volt font-semibold">{INR(wonMTD)} won MTD</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Renewal window (Super Admin can change; others see it read-only) */}
          <div className="flex items-center gap-2 bg-[#0a0a0a] border border-zinc-900 rounded-sm px-3 py-2" data-testid="renewal-window-block">
            <span className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Renewal window</span>
            <Select
              value={String(settings?.renewal_window_days || 30)}
              onValueChange={(v) => updateSettings.mutate({ renewal_window_days: Number(v) })}
              disabled={user?.role !== "SUPER_ADMIN"}
            >
              <SelectTrigger className="bg-zinc-950 border-zinc-800 h-7 text-xs w-[110px]" data-testid="renewal-window-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="14">14 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="45">45 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
              </SelectContent>
            </Select>
            {(user?.role === "SUPER_ADMIN" || user?.role === "ADMIN") && (
              <Button
                variant="outline" size="sm"
                onClick={refreshRenewals}
                disabled={refreshingRenewals}
                data-testid="refresh-renewals-btn"
                className="h-7 bg-zinc-950 border-zinc-800 hover:border-volt text-zinc-300 hover:text-volt uppercase tracking-[0.14em] text-[10px]"
              >
                <RefreshCw size={11} className={`mr-1 ${refreshingRenewals ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            )}
          </div>
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button className="bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm" data-testid="new-lead-button">
                <Plus size={16} className="mr-1" /> New Lead
              </Button>
            </DialogTrigger>
            <NewLeadDialog
              branches={branches}
              coaches={coaches}
              defaultKind={pipelineFilter === "RENEWAL" ? "RENEWAL" : "LEAD"}
              onSubmit={(b) => createLead.mutate(b)}
            />
          </Dialog>
        </div>
      </div>

      {/* Pipeline filter — sits just below the branch selector (top-bar).
          Purely a view filter; server still returns every lead. */}
      <div className="flex items-center gap-2" data-testid="pipeline-filter">
        <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Pipeline</span>
        <div className="inline-flex bg-[#0a0a0a] border border-zinc-900 rounded-sm p-0.5">
          {[
            { key: "ALL",     label: "All",        count: leads.length },
            { key: "LEAD",    label: "New Leads",  count: leads.filter((l) => (l.kind || "LEAD") === "LEAD").length },
            { key: "RENEWAL", label: "Renewals",   count: leads.filter((l) => l.kind === "RENEWAL").length },
          ].map((opt) => {
            const active = pipelineFilter === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => setPipelineFilter(opt.key)}
                data-testid={`pipeline-filter-${opt.key.toLowerCase()}`}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold rounded-sm transition-colors ${
                  active
                    ? "bg-volt text-black"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                {opt.label}
                <span className={`ml-2 text-[10px] ${active ? "text-black/60" : "text-zinc-600"}`}>
                  {opt.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Kanban */}
      {/* Kanban — column set depends on the active pipeline filter.
          LEAD view = new-lead columns, RENEWAL view = renewal-only columns,
          ALL view = the full union so nothing is ever hidden. */}
      <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-thin" data-testid="kanban-board">
        {(pipelineFilter === "LEAD"
          ? NEW_LEAD_STAGES
          : pipelineFilter === "RENEWAL"
          ? RENEWAL_STAGES
          : STAGES
        ).map((stage) => {
          const items = visibleLeads.filter((l) => l.stage === stage);
          const meta = STAGE_META[stage];
          return (
            <div
              key={stage}
              className="kanban-col flex-shrink-0 bg-[#0a0a0a] border border-zinc-900 rounded-md flex flex-col"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => onDrop(e, stage)}
              data-testid={`stage-col-${stage}`}
            >
              <div className="p-3 border-b border-zinc-900 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: meta.color, boxShadow: `0 0 8px ${meta.color}` }} />
                  <div className="text-xs uppercase tracking-[0.16em] font-bold">{stage === "NEW" ? "Act Now" : stage === "PROPOSAL_SENT" ? "Negotiating" : meta.label}</div>
                </div>
                <span className="font-display text-lg font-black text-zinc-400">{items.length}</span>
              </div>
              <div className="p-2 space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto scrollbar-thin">
                {items.map((l) => {
                  const isRenewal = l.kind === "RENEWAL";
                  const heat = calcHeat(l);
                  const program = programChipLabel(l);
                  const renewalOn = isRenewal ? fmtRenewalDate(l.renewal_expiry_date) : null;
                  const openCard = () => {
                    if (isRenewal) {
                      const q = l.phone || l.name || "";
                      navigate(`/members?q=${encodeURIComponent(q)}`);
                    } else {
                      setSelectedLead(l);
                    }
                  };
                  return (
                    <article
                      key={l.id}
                      draggable
                      onDragStart={(e) => onDragStart(e, l.id)}
                      onClick={openCard}
                      data-testid={`lead-card-${l.id}`}
                      data-kind={isRenewal ? "RENEWAL" : "LEAD"}
                      data-heat={heat.level}
                      className={`bg-[#121212] border rounded-sm p-3 cursor-pointer hover:-translate-y-[1px] transition-all duration-150 ${
                        isRenewal
                          ? "border-amber-600/40 hover:border-amber-400"
                          : "border-zinc-800 hover:border-volt"
                      }`}
                    >
                      {/* Row 1: name + heat dot */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-semibold text-sm truncate">{l.name}</div>
                        <span
                          data-testid={`heat-dot-${l.id}`}
                          title={heat.label}
                          className="inline-block w-2 h-2 rounded-full mt-1.5 shrink-0"
                          style={{ background: heat.color, boxShadow: `0 0 6px ${heat.color}` }}
                        />
                      </div>

                      {/* Row 2: kind tag + program tag (side by side) */}
                      <div className="flex flex-wrap items-center gap-1 mt-1.5">
                        {isRenewal ? (
                          <span
                            data-testid={`tag-renewal-${l.id}`}
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.14em] font-bold px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-300 border border-amber-500/40"
                          >
                            <RefreshCw size={9} /> Renewal Due
                          </span>
                        ) : (
                          <span
                            data-testid={`tag-newlead-${l.id}`}
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.14em] font-bold px-1.5 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                          >
                            <Sparkles size={9} /> New Lead
                          </span>
                        )}
                        {program && (
                          <span
                            data-testid={`tag-program-${l.id}`}
                            className="inline-flex items-center text-[9px] uppercase tracking-[0.14em] font-bold px-1.5 py-0.5 rounded-sm bg-zinc-800/80 text-zinc-300 border border-zinc-700"
                          >
                            {program}
                          </span>
                        )}
                      </div>

                      {/* Row 3: renewal date (renewals) OR source (leads) */}
                      {isRenewal ? (
                        renewalOn && (
                          <div className="flex items-center gap-1 mt-2 text-[11px] text-amber-300/90" data-testid={`renewal-on-${l.id}`}>
                            <span className="text-[9px] uppercase tracking-[0.16em] text-zinc-500">Renews</span>
                            <span className="font-bold">{renewalOn}</span>
                          </div>
                        )
                      ) : (
                        <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mt-2">
                          {SOURCE_LABEL[l.source] || l.source}
                        </div>
                      )}

                      {/* Row 4: budget + phone */}
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[10px] text-zinc-500">{INR(l.budget)}</span>
                        {l.score != null && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-volt/15 text-volt">{l.score}</span>
                        )}
                      </div>
                    </article>
                  );
                })}
                {items.length === 0 && (
                  <div className="text-center text-[10px] uppercase tracking-[0.16em] text-zinc-700 py-8">Empty</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Lead drawer */}
      <Sheet open={!!selectedLead} onOpenChange={(o) => !o && setSelectedLead(null)}>
        {selectedLead && (
          <SheetContent side="right" className="bg-[#0a0a0a] border-l border-zinc-900 w-full sm:max-w-md text-white">
            <LeadDetail
              lead={selectedLead}
              branches={branches}
              coaches={coaches}
              canDelete={user?.role === "SUPER_ADMIN" || user?.role === "ADMIN"}
              canEdit={user?.role === "SUPER_ADMIN" || user?.role === "ADMIN"}
              onUpdate={(body, opts = {}) =>
                updateLead.mutate({ id: selectedLead.id, body }, {
                  onSuccess: (updated) => {
                    toast.success("Saved");
                    setSelectedLead((prev) => prev ? { ...prev, ...(updated || body) } : prev);
                    opts.onSuccess?.(updated);
                  },
                  onError: (e) => {
                    toast.error(e?.response?.data?.detail || "Save failed");
                    opts.onError?.(e);
                  },
                })
              }
              onDelete={async () => {
                if (!window.confirm(`Delete lead "${selectedLead.name}"? This cannot be undone.`)) return;
                try {
                  await apiClient.delete(`/leads/${selectedLead.id}`);
                  toast.success("Lead deleted");
                  setSelectedLead(null);
                  qc.invalidateQueries({ queryKey: ["leads"] });
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Delete failed");
                }
              }}
              onAISummary={async () => {
                try {
                  const { data } = await apiClient.post(`/leads/${selectedLead.id}/ai-summary`);
                  toast.success("AI brief generated");
                  setSelectedLead({ ...selectedLead, ai_summary: data.summary });
                  qc.invalidateQueries({ queryKey: ["leads"] });
                } catch (e) {
                  toast.error("AI failed");
                }
              }}
              currentUser={user}
              onLeadRefreshed={(updated) => {
                setSelectedLead((prev) => prev ? { ...prev, ...updated } : prev);
                qc.invalidateQueries({ queryKey: ["leads"] });
              }}
            />
          </SheetContent>
        )}
      </Sheet>
    </div>
  );
}

function NewLeadDialog({ branches, coaches, onSubmit, defaultKind = "LEAD" }) {
  const { activeBranchId } = useAuth();
  const [config, setConfig] = React.useState(null);
  const [slots, setSlots] = React.useState([]);
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", email: "",
    branch_id: activeBranchId || branches[0]?.id || "",
    source: "WEBSITE",
    kind: defaultKind,                              // NEW: pipeline classifier
    athlete_profile: "", athlete_profile_other: "",
    heard_from: "", heard_from_other: "",
    programme_code: "", programme_other: "",
    preferred_start_date: "",
    is_online: false, location_other: "", preferred_time_slot: "",
    referred_by_phone: "",
    fitness_goal: "", budget: "", notes: "", assigned_to: "",
  });

  React.useEffect(() => {
    apiClient.get("/public/lead-form-config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const isValidBranch = form.branch_id && form.branch_id !== "__online__";
  const showTimeSlot = isValidBranch && !form.is_online && !!form.programme_code;

  React.useEffect(() => {
    if (!showTimeSlot) { setSlots([]); return; }
    const params = new URLSearchParams({ branch_id: form.branch_id });
    if (form.programme_code) params.set("session_type", form.programme_code);
    apiClient.get(`/public/time-slots?${params.toString()}`)
      .then((r) => setSlots(r.data)).catch(() => setSlots([]));
  }, [showTimeSlot, form.branch_id, form.programme_code]);

  // Preferred Program: restricted to the 5 core class types the gym offers
  // (Strength, Conditioning, MMA, Hyrox, Open Gym). Anything else in the
  // catalog is treated as a marketing "interest" and not exposed here.
  const CORE_PROGRAM_CODES = React.useMemo(
    () => ["STRENGTH", "CONDITIONING", "MMA", "HYROX", "OPEN_GYM"],
    []
  );
  const programOptions = React.useMemo(() => {
    const label = { STRENGTH: "Strength", CONDITIONING: "Conditioning",
                    MMA: "MMA", HYROX: "Hyrox", OPEN_GYM: "Open Gym" };
    return CORE_PROGRAM_CODES.map((code) => ({ code, label: label[code] }));
  }, [CORE_PROGRAM_CODES]);

  // Deduped, time-only labels for the Preferred Timeslot dropdown.
  // Each start-time appears exactly once regardless of how many days it runs.
  // Open Gym returns a single flexible row from the API — surface it as "Anytime (walk-in)".
  const timeOptions = React.useMemo(() => {
    if (!slots.length) return [];
    if (form.programme_code === "OPEN_GYM") {
      const s = slots[0];
      const range = s?.start && s?.end
        ? `${fmt12hLead(s.start)} – ${fmt12hLead(s.end)}` : "";
      return [{ value: s?.label || "OPEN_GYM_FLEX", label: `Anytime (walk-in)${range ? ` · ${range}` : ""}` }];
    }
    const uniqStarts = Array.from(new Set(slots.map((s) => s.start).filter(Boolean))).sort();
    return uniqStarts.map((t) => ({ value: t, label: fmt12hLead(t) }));
  }, [slots, form.programme_code]);

  const upd = (k, v) => setForm((x) => ({ ...x, [k]: v }));

  const submit = () => {
    if (!form.first_name.trim() || !form.last_name.trim()) { toast.error("First & Last name required"); return; }
    if (!form.phone || form.phone.replace(/\D/g, "").length < 10) { toast.error("Valid phone required (10+ digits)"); return; }
    if (!form.branch_id && !form.is_online) { toast.error("Pick a branch or Online"); return; }
    if (form.referred_by_phone) {
      const rd = form.referred_by_phone.replace(/\D/g, "");
      if (rd.length !== 10) { toast.error("Referrer phone must be 10 digits"); return; }
    }
    const programme_label = programOptions.find((p) => p.code === form.programme_code)?.label || null;
    onSubmit({
      name: `${form.first_name.trim()} ${form.last_name.trim()}`.trim(),
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim() || null,
      branch_id: form.branch_id || (branches[0]?.id),
      source: form.source,
      kind: form.kind || "LEAD",
      program_interest: programme_label || form.programme_code || null,
      programme_code: form.programme_code || null,
      programme_other: null,
      athlete_profile: form.athlete_profile || null,
      athlete_profile_other: form.athlete_profile === "Other" ? form.athlete_profile_other.trim() : null,
      heard_from: form.heard_from || null,
      heard_from_other: form.heard_from === "Other" ? form.heard_from_other.trim() : null,
      preferred_start_date: form.preferred_start_date || null,
      is_online: form.is_online,
      location_other: form.location_other.trim() || null,
      preferred_time_slot: showTimeSlot ? (form.preferred_time_slot || null) : null,
      referred_by_phone: form.referred_by_phone.trim() || null,
      fitness_goal: form.fitness_goal.trim() || null,
      budget: form.budget ? Number(form.budget) : null,
      notes: form.notes.trim() || null,
      assigned_to: form.assigned_to || null,
    });
  };

  return (
    <DialogContent className="bg-[#0a0a0a] border-zinc-800 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="new-lead-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl uppercase tracking-tight">New Lead</DialogTitle>
        <DialogDescription className="text-zinc-500 text-xs">Same fields as the website lead form — kept in sync so all leads look identical in the pipeline.</DialogDescription>
      </DialogHeader>
      <div className="grid grid-cols-2 gap-3">
        <Field label="First Name *"><Input data-testid="lead-first-name-input" value={form.first_name} onChange={(e) => upd("first_name", e.target.value)} className="bg-zinc-950 border-zinc-800" /></Field>
        <Field label="Last Name *"><Input data-testid="lead-last-name-input" value={form.last_name} onChange={(e) => upd("last_name", e.target.value)} className="bg-zinc-950 border-zinc-800" /></Field>
        <Field label="Phone *"><Input data-testid="lead-phone-input" value={form.phone} onChange={(e) => upd("phone", e.target.value)} className="bg-zinc-950 border-zinc-800" /></Field>
        <Field label="Email"><Input value={form.email} onChange={(e) => upd("email", e.target.value)} className="bg-zinc-950 border-zinc-800" /></Field>
        <Field label="Branch">
          <Select value={form.is_online ? "__online__" : form.branch_id}
                  onValueChange={(v) => {
                    if (v === "__online__") setForm((x) => ({ ...x, is_online: true, branch_id: "", preferred_time_slot: "" }));
                    else setForm((x) => ({ ...x, is_online: false, branch_id: v, preferred_time_slot: "" }));
                  }}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="lead-branch"><SelectValue /></SelectTrigger>
            <SelectContent>
              {branches.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
              <SelectItem value="__online__">Online / Remote</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Preferred Program" full>
          <Select
            value={form.programme_code}
            onValueChange={(v) => setForm((x) => ({ ...x, programme_code: v, preferred_time_slot: "" }))}
          >
            <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="lead-preferred-program">
              <SelectValue placeholder="— select a program —" />
            </SelectTrigger>
            <SelectContent>
              {programOptions.map((p) => (
                <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        {showTimeSlot && (
          <Field label="Preferred Timeslot" full>
            <Select value={form.preferred_time_slot} onValueChange={(v) => upd("preferred_time_slot", v)}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="lead-preferred-timeslot">
                <SelectValue placeholder={timeOptions.length ? "— pick a time —" : "No times available for this program at this branch"} />
              </SelectTrigger>
              <SelectContent>
                {timeOptions.length === 0 ? (
                  <SelectItem value="__none__" disabled>No live schedule matches</SelectItem>
                ) : timeOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`lead-timeslot-opt-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        )}
        <Field label="Source">
          <Select value={form.source} onValueChange={(v) => upd("source", v)}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(SOURCE_LABEL).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}</SelectContent>
          </Select>
        </Field>
        <Field label="Pipeline">
          <Select value={form.kind} onValueChange={(v) => upd("kind", v)}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="lead-kind-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="LEAD">New Lead</SelectItem>
              <SelectItem value="RENEWAL">Renewal</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="What best describes them?" full>
          <Select value={form.athlete_profile} onValueChange={(v) => upd("athlete_profile", v)}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="lead-athlete"><SelectValue placeholder="— select —" /></SelectTrigger>
            <SelectContent>{(config?.athlete_profiles || []).map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
          </Select>
          {form.athlete_profile === "Other" && (
            <Input data-testid="lead-athlete-other" value={form.athlete_profile_other} onChange={(e) => upd("athlete_profile_other", e.target.value)} placeholder="What describes them?" className="bg-zinc-950 border-zinc-800 mt-2" />
          )}
        </Field>
        <Field label="Heard from" full>
          <Select value={form.heard_from} onValueChange={(v) => upd("heard_from", v)}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="lead-heard"><SelectValue placeholder="— select —" /></SelectTrigger>
            <SelectContent>{(config?.heard_from_options || []).map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
          </Select>
          {form.heard_from === "Other" && (
            <Input data-testid="lead-heard-other" value={form.heard_from_other} onChange={(e) => upd("heard_from_other", e.target.value)} placeholder="Please specify" className="bg-zinc-950 border-zinc-800 mt-2" />
          )}
        </Field>
        <Field label="Preferred Start Date">
          <Input type="date" value={form.preferred_start_date} onChange={(e) => upd("preferred_start_date", e.target.value)} data-testid="lead-startdate" className="bg-zinc-950 border-zinc-800" />
        </Field>
        <Field label="Referred By (10-digit phone)" full>
          <Input data-testid="lead-referred-phone" value={form.referred_by_phone} onChange={(e) => upd("referred_by_phone", e.target.value)} placeholder="9XXXXXXXXX" maxLength={13} className="bg-zinc-950 border-zinc-800" />
          <div className="text-[10px] text-zinc-600 mt-1">Auto-links to the existing member/coach if their phone matches. Used as the primary identifier for referral rewards.</div>
        </Field>
        <Field label="Fitness Goal"><Input value={form.fitness_goal} onChange={(e) => upd("fitness_goal", e.target.value)} className="bg-zinc-950 border-zinc-800" /></Field>
        <Field label="Budget (₹)"><Input type="number" value={form.budget} onChange={(e) => upd("budget", e.target.value)} className="bg-zinc-950 border-zinc-800" /></Field>
        <Field label="Assign Coach">
          <Select value={form.assigned_to} onValueChange={(v) => upd("assigned_to", v)}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800"><SelectValue placeholder="(unassigned)" /></SelectTrigger>
            <SelectContent>{coaches.map((c) => (<SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>))}</SelectContent>
          </Select>
        </Field>
        <Field label="Notes" full><Textarea value={form.notes} onChange={(e) => upd("notes", e.target.value)} className="bg-zinc-950 border-zinc-800" /></Field>
      </div>
      <DialogFooter>
        <Button onClick={submit} className="bg-volt hover:bg-volt-hover text-black font-bold uppercase" data-testid="submit-new-lead">Create Lead</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function LeadDetail({ lead, branches, coaches, onUpdate, onAISummary, canDelete, canEdit, onDelete, currentUser, onLeadRefreshed }) {
  const branch = branches.find((b) => b.id === lead.branch_id);
  const coach = coaches.find((c) => c.id === lead.assigned_to);
  const [busyAI, setBusyAI] = useState(false);
  const [budgetPromptFor, setBudgetPromptFor] = useState(null); // target stage awaiting budget
  const [budgetVal, setBudgetVal] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const meta = STAGE_META[lead.stage];

  const handleAI = async () => {
    setBusyAI(true);
    await onAISummary();
    setBusyAI(false);
  };

  const needsBudget = lead.source === "WEBSITE_FORM" && (!lead.budget || Number(lead.budget) <= 0);
  const tryMoveStage = (s) => {
    if (s === lead.stage) return;
    if (lead.stage === "NEW" && s !== "NEW" && needsBudget) {
      setBudgetPromptFor(s);
      setBudgetVal("");
      return;
    }
    onUpdate({ stage: s });
  };
  const saveBudgetAndMove = () => {
    const n = Number(budgetVal);
    if (!n || n <= 0) { toast.error("Please enter a valid budget"); return; }
    onUpdate({ budget: n, stage: budgetPromptFor });
    setBudgetPromptFor(null);
  };

  return (
    <div className="h-full flex flex-col">
      <SheetHeader>
        <div className="flex items-start justify-between gap-2">
          <SheetTitle className="font-display text-3xl uppercase tracking-tight">{lead.name}</SheetTitle>
          {canEdit && (
            <Button
              variant="outline" size="sm"
              onClick={() => setEditOpen(true)}
              data-testid={`edit-lead-btn-${lead.id}`}
              className="bg-zinc-950 border-zinc-800 hover:border-volt text-zinc-300 hover:text-volt rounded-sm uppercase tracking-[0.14em] text-[10px] shrink-0">
              <Pencil size={11} className="mr-1" /> Edit
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-[10px] font-bold px-2 py-1 rounded-sm" style={{ background: `${meta.color}22`, color: meta.color }}>{meta.label}</span>
          <span className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">{SOURCE_LABEL[lead.source]}</span>
        </div>
      </SheetHeader>
      <div className="space-y-4 mt-6 overflow-y-auto scrollbar-thin pr-1 flex-1">
        <Section label="Contact">
          <div className="flex items-center gap-2 text-sm"><Phone size={14} className="text-zinc-500" /> {lead.phone}</div>
          {lead.email && <div className="flex items-center gap-2 text-sm"><Mail size={14} className="text-zinc-500" /> {lead.email}</div>}
        </Section>

        <Section label="Pipeline">
          <div className="grid grid-cols-2 gap-2">
            <div className="text-xs text-zinc-500">Branch<div className="text-white text-sm font-medium">{branch?.name}</div></div>
            <div className="text-xs text-zinc-500">Coach<div className="text-white text-sm font-medium">{coach?.full_name || "—"}</div></div>
            <div className="text-xs text-zinc-500">Budget<div className="text-volt font-display text-xl font-bold">{INR(lead.budget)}</div></div>
            <div className="text-xs text-zinc-500">Score<div className="text-volt font-display text-xl font-bold">{lead.score ?? "—"}</div></div>
          </div>
        </Section>

        <Section label="Move to stage">
          {needsBudget && lead.stage === "NEW" && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 text-[11px] uppercase tracking-[0.14em] font-bold p-2 rounded-sm mb-2" data-testid="budget-required-hint">
              Budget required · This lead came from the Website form — add a budget before moving stages.
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            {STAGES.filter((s) => {
              // Renewals can't move to LOST; new leads can't move to DROPPED_OUT
              if (lead.kind === "RENEWAL" && s === "LOST") return false;
              if (lead.kind !== "RENEWAL" && s === "DROPPED_OUT") return false;
              return true;
            }).map((s) => (
              <button
                key={s}
                onClick={() => tryMoveStage(s)}
                data-testid={`move-stage-${s}`}
                className={`text-[10px] uppercase tracking-[0.16em] py-2 rounded-sm border transition-colors ${
                  s === lead.stage ? "border-volt text-volt bg-volt/10" : "border-zinc-800 hover:border-zinc-600 text-zinc-300"
                }`}
              >
                {STAGE_META[s].label}
              </button>
            ))}
          </div>
        </Section>

        <Section label="AI Brief">
          <Button onClick={handleAI} disabled={busyAI} variant="outline"
            className="bg-zinc-900 border-zinc-800 hover:border-volt text-volt" data-testid="ai-summary-button">
            <Sparkles size={14} className="mr-2" />{busyAI ? "Thinking…" : "Generate AI brief"}
          </Button>
          {lead.ai_summary && (
            <div className="mt-3 text-sm text-zinc-300 leading-relaxed bg-zinc-950 border border-zinc-900 p-3 rounded-sm whitespace-pre-wrap" data-testid="ai-summary-text">
              {lead.ai_summary}
            </div>
          )}
        </Section>

        <CommentsSection
          lead={lead}
          currentUser={currentUser}
          onLeadRefreshed={onLeadRefreshed}
        />

        {lead.stage === "WON" && (
          <>
            <Section label="Start Date">
              <Input
                type="date"
                defaultValue={lead.converted_start_date || new Date().toISOString().slice(0, 10)}
                onBlur={(e) => e.target.value && onUpdate({ converted_start_date: e.target.value })}
                data-testid="won-start-date"
                className="bg-zinc-950 border-zinc-800"
              />
              <div className="text-[10px] text-zinc-600 mt-1">Auto-set to today when moved to WON — edit here if you&apos;re logging the sale a day late. This flows through when you create the membership.</div>
            </Section>
            <Section label="Won Revenue">
              <Input
                type="number" defaultValue={lead.won_revenue || ""}
                onBlur={(e) => onUpdate({ won_revenue: Number(e.target.value) || 0 })}
                className="bg-zinc-950 border-zinc-800"
              />
            </Section>
            <ConvertToMemberSection lead={lead} onConverted={onUpdate} />
          </>
        )}
      </div>
      {canDelete && (
        <div className="mt-4 pt-4 border-t border-zinc-900">
          <Button
            variant="outline"
            onClick={onDelete}
            data-testid={`delete-lead-${lead.id}`}
            className="w-full bg-zinc-950 border-red-900/60 text-red-400 hover:border-red-500 hover:text-red-300 rounded-sm uppercase tracking-[0.16em] text-xs">
            <X size={12} className="mr-1" /> Delete Lead
          </Button>
          <div className="text-[10px] text-zinc-600 mt-2 leading-relaxed">
            Direct delete — for cleaning up manual-entry errors. Use with care.
          </div>
        </div>
      )}
      {/* Budget prompt — required to move a WEBSITE_FORM lead out of NEW */}
      <Dialog open={!!budgetPromptFor} onOpenChange={(o) => !o && setBudgetPromptFor(null)}>
        <DialogContent className="bg-[#0a0a0a] border-zinc-800 text-white max-w-md" data-testid="budget-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase tracking-tight">Set Lead Budget</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              Website-form leads need a budget before moving to <span className="text-volt">{budgetPromptFor && STAGE_META[budgetPromptFor]?.label}</span>. Enter what the pipeline manager expects this lead to convert at.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Budget (₹) *</Label>
            <Input type="number" min="0" value={budgetVal} onChange={(e) => setBudgetVal(e.target.value)}
              placeholder="e.g. 12000" autoFocus data-testid="budget-input"
              className="bg-zinc-950 border-zinc-800 mt-1" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBudgetPromptFor(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={saveBudgetAndMove} disabled={!budgetVal || Number(budgetVal) <= 0}
              data-testid="save-budget-and-move"
              className="bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-[0.14em]">
              Save & Move
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full "Edit Lead" dialog — identity, contact, branch, program, timeslot */}
      <EditLeadDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        lead={lead}
        branches={branches}
        onSubmit={(body) => onUpdate(body, { onSuccess: () => setEditOpen(false) })}
      />
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2 font-semibold">{label}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

// Fine-grained team notes thread on a lead. Every staff role can post; delete
// is limited to author + Super Admin (enforced server-side).
function CommentsSection({ lead, currentUser, onLeadRefreshed }) {
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const comments = Array.isArray(lead.comments) ? lead.comments : [];
  // Newest first for display; server pushes append, so we reverse a copy.
  const ordered = [...comments].reverse();

  const post = async () => {
    const t = text.trim();
    if (!t) return;
    setPosting(true);
    try {
      const { data } = await apiClient.post(`/leads/${lead.id}/comments`, { text: t });
      setText("");
      onLeadRefreshed?.(data);
      toast.success("Comment added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Post failed");
    } finally {
      setPosting(false);
    }
  };

  const remove = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    setDeletingId(commentId);
    try {
      const { data } = await apiClient.delete(`/leads/${lead.id}/comments/${commentId}`);
      onLeadRefreshed?.(data);
      toast.success("Comment deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const fmtWhen = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "numeric", minute: "2-digit" });
    } catch { return iso; }
  };
  const canDeleteComment = (c) =>
    currentUser?.role === "SUPER_ADMIN" || c.author_id === currentUser?.id;

  return (
    <Section label={`Comments${comments.length ? ` · ${comments.length}` : ""}`}>
      <div className="space-y-2" data-testid="lead-comments">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a personal note for your team… (e.g. followed up on WhatsApp, prefers evening trial)"
          rows={2}
          className="bg-zinc-950 border-zinc-800 text-sm resize-none"
          data-testid="lead-comment-input"
        />
        <div className="flex items-center justify-between">
          <div className="text-[10px] text-zinc-600">{text.length}/2000</div>
          <Button
            onClick={post}
            disabled={posting || !text.trim()}
            size="sm"
            className="bg-volt text-black hover:bg-volt/90 rounded-sm uppercase tracking-[0.14em] text-[10px] font-bold disabled:opacity-40"
            data-testid="lead-comment-post-btn"
          >
            {posting ? "Posting…" : "Post"}
          </Button>
        </div>

        {ordered.length === 0 ? (
          <div className="text-[11px] text-zinc-600 italic py-2">No comments yet — start the thread.</div>
        ) : (
          <div className="space-y-2 mt-2">
            {ordered.map((c) => (
              <div
                key={c.id}
                className="bg-zinc-950 border border-zinc-900 p-2.5 rounded-sm group"
                data-testid={`lead-comment-${c.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-volt font-semibold">
                    {c.author_name}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-zinc-600">{fmtWhen(c.created_at)}</span>
                    {canDeleteComment(c) && (
                      <button
                        onClick={() => remove(c.id)}
                        disabled={deletingId === c.id}
                        className="text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-40"
                        data-testid={`lead-comment-delete-${c.id}`}
                        aria-label="Delete comment"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed">{c.text}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}

const CORE_PROGRAMS_EDIT = [
  { code: "STRENGTH", label: "Strength" },
  { code: "CONDITIONING", label: "Conditioning" },
  { code: "MMA", label: "MMA" },
  { code: "HYROX", label: "Hyrox" },
  { code: "OPEN_GYM", label: "Open Gym" },
];

function EditLeadDialog({ open, onClose, lead, branches, onSubmit }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", email: "",
    branch_id: "", programme_code: "", preferred_time_slot: "",
    source: "WEBSITE", fitness_goal: "", budget: "", kind: "LEAD",
  });
  const [slots, setSlots] = useState([]);

  React.useEffect(() => {
    if (!open || !lead) return;
    // Derive first/last from `name` if the row was captured before the split fields existed
    const parts = (lead.name || "").trim().split(/\s+/);
    const fn = lead.first_name || parts[0] || "";
    const ln = lead.last_name  || (parts.length > 1 ? parts.slice(1).join(" ") : "");
    setForm({
      first_name: fn, last_name: ln,
      phone: lead.phone || "", email: lead.email || "",
      branch_id: lead.branch_id || "",
      programme_code: lead.programme_code || "",
      preferred_time_slot: lead.preferred_time_slot || "",
      source: lead.source || "WEBSITE",
      fitness_goal: lead.fitness_goal || "",
      budget: lead.budget != null ? String(lead.budget) : "",
      kind: lead.kind || "LEAD",
    });
  }, [open, lead]);

  // Live timeslot lookup based on selected branch × program
  React.useEffect(() => {
    if (!open || !form.branch_id || !form.programme_code) { setSlots([]); return; }
    const params = new URLSearchParams({ branch_id: form.branch_id, session_type: form.programme_code });
    apiClient.get(`/public/time-slots?${params.toString()}`)
      .then((r) => setSlots(r.data || [])).catch(() => setSlots([]));
  }, [open, form.branch_id, form.programme_code]);

  const timeOptions = React.useMemo(() => {
    if (!slots.length) return [];
    if (form.programme_code === "OPEN_GYM") {
      const s = slots[0];
      const range = s?.start && s?.end ? `${fmt12hLead(s.start)} – ${fmt12hLead(s.end)}` : "";
      return [{ value: s?.label || "OPEN_GYM_FLEX", label: `Anytime (walk-in)${range ? ` · ${range}` : ""}` }];
    }
    const uniq = Array.from(new Set(slots.map((s) => s.start).filter(Boolean))).sort();
    return uniq.map((t) => ({ value: t, label: fmt12hLead(t) }));
  }, [slots, form.programme_code]);

  const upd = (k, v) => setForm((x) => ({ ...x, [k]: v }));

  const save = () => {
    if (!form.first_name.trim()) { toast.error("First name is required"); return; }
    if (!form.phone.trim()) { toast.error("Phone is required"); return; }
    if (!form.branch_id) { toast.error("Branch is required"); return; }

    // Build a delta payload — only send fields that changed, so we don't
    // accidentally overwrite server-side defaults or fire irrelevant approvals.
    const original = {
      first_name: lead.first_name || "",
      last_name: lead.last_name || "",
      phone: lead.phone || "",
      email: lead.email || "",
      branch_id: lead.branch_id || "",
      programme_code: lead.programme_code || "",
      preferred_time_slot: lead.preferred_time_slot || "",
      source: lead.source || "",
      fitness_goal: lead.fitness_goal || "",
      budget: lead.budget != null ? String(lead.budget) : "",
      kind: lead.kind || "LEAD",
    };
    const body = {};
    for (const k of Object.keys(original)) {
      const cur = (form[k] || "").toString().trim();
      const orig = (original[k] || "").toString().trim();
      if (cur !== orig) body[k] = k === "budget" ? Number(cur) || 0 : cur;
    }
    if (Object.keys(body).length === 0) { toast.info("No changes to save"); onClose(); return; }
    // Auto-sync the human-readable programme_label whenever the code changes,
    // so downstream views (pipeline cards, revenue exports) stay consistent.
    if ("programme_code" in body) {
      body.programme_label = CORE_PROGRAMS_EDIT.find((p) => p.code === body.programme_code)?.label || null;
    }
    onSubmit(body);
  };

  const programLabel = CORE_PROGRAMS_EDIT.find((p) => p.code === form.programme_code)?.label;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0a0a0a] border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight">Edit Lead</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Update contact details, placement, or program preference. Stage, notes, and coach assignment are edited from the main panel.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">First Name *</Label>
              <Input value={form.first_name} onChange={(e) => upd("first_name", e.target.value)}
                data-testid="edit-lead-first-name" className="bg-zinc-950 border-zinc-800 mt-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Last Name</Label>
              <Input value={form.last_name} onChange={(e) => upd("last_name", e.target.value)}
                data-testid="edit-lead-last-name" className="bg-zinc-950 border-zinc-800 mt-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Phone *</Label>
              <Input value={form.phone} onChange={(e) => upd("phone", e.target.value)}
                data-testid="edit-lead-phone" className="bg-zinc-950 border-zinc-800 mt-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Email</Label>
              <Input value={form.email} onChange={(e) => upd("email", e.target.value)}
                data-testid="edit-lead-email" className="bg-zinc-950 border-zinc-800 mt-1" />
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Branch *</Label>
            <Select value={form.branch_id}
              onValueChange={(v) => setForm((x) => ({ ...x, branch_id: v, preferred_time_slot: "" }))}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1" data-testid="edit-lead-branch">
                <SelectValue placeholder="— select a branch —" />
              </SelectTrigger>
              <SelectContent>
                {branches.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Preferred Program</Label>
              <Select value={form.programme_code}
                onValueChange={(v) => setForm((x) => ({ ...x, programme_code: v, preferred_time_slot: "" }))}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1" data-testid="edit-lead-program">
                  <SelectValue placeholder="— select —" />
                </SelectTrigger>
                <SelectContent>
                  {CORE_PROGRAMS_EDIT.map((p) => (<SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Preferred Timeslot</Label>
              <Select value={form.preferred_time_slot} onValueChange={(v) => upd("preferred_time_slot", v)}
                disabled={!form.programme_code || !form.branch_id}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1" data-testid="edit-lead-timeslot">
                  <SelectValue placeholder={form.programme_code ? (timeOptions.length ? "— pick a time —" : "No times available") : "Pick a program first"} />
                </SelectTrigger>
                <SelectContent>
                  {timeOptions.length === 0 ? (
                    <SelectItem value="__none__" disabled>No live schedule matches</SelectItem>
                  ) : timeOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Source</Label>
              <Select value={form.source} onValueChange={(v) => upd("source", v)}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1" data-testid="edit-lead-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(SOURCE_LABEL).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Budget (₹)</Label>
              <Input type="number" min="0" value={form.budget} onChange={(e) => upd("budget", e.target.value)}
                data-testid="edit-lead-budget" className="bg-zinc-950 border-zinc-800 mt-1" />
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Pipeline</Label>
            <Select value={form.kind} onValueChange={(v) => upd("kind", v)}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1" data-testid="edit-lead-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="LEAD">New Lead</SelectItem>
                <SelectItem value="RENEWAL">Renewal</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Fitness Goal</Label>
            <Input value={form.fitness_goal} onChange={(e) => upd("fitness_goal", e.target.value)}
              placeholder="Fat loss, muscle gain, sports performance, etc."
              data-testid="edit-lead-goal" className="bg-zinc-950 border-zinc-800 mt-1" />
          </div>

          {programLabel && (
            <div className="text-[10px] text-zinc-500">
              Program label auto-syncs to <span className="text-volt">{programLabel}</span> on save.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="edit-lead-cancel">Cancel</Button>
          <Button onClick={save} className="bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-[0.14em]"
            data-testid="edit-lead-save">
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConvertToMemberSection({ lead, onConverted }) {
  const [open, setOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [slots, setSlots] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [form, setForm] = useState({
    plan_name: "", category: "", price: "", duration_days: 90, total_sessions: 0,
    start_date: lead.converted_start_date || new Date().toISOString().slice(0, 10),
    preferred_program: lead.programme_code || "",
    preferred_timeslot: lead.preferred_time_slot || "",
  });
  const qc = useQueryClient();
  React.useEffect(() => {
    if (!open) return;
    apiClient.get("/plans").then((r) => setPlans((r.data || []).filter((p) => p.active !== false))).catch(() => setPlans([]));
    setForm((x) => ({
      ...x,
      start_date: lead.converted_start_date || new Date().toISOString().slice(0, 10),
      preferred_program: lead.programme_code || "",
      preferred_timeslot: lead.preferred_time_slot || "",
    }));
  }, [open, lead.converted_start_date, lead.programme_code, lead.preferred_time_slot]);

  // Live-load timeslots whenever the (branch, preferred_program) pair changes.
  React.useEffect(() => {
    if (!open || !lead.branch_id || !form.preferred_program) { setSlots([]); return; }
    const params = new URLSearchParams({ branch_id: lead.branch_id, session_type: form.preferred_program });
    apiClient.get(`/public/time-slots?${params.toString()}`)
      .then((r) => setSlots(r.data || [])).catch(() => setSlots([]));
  }, [open, lead.branch_id, form.preferred_program]);

  const timeOptions = React.useMemo(() => {
    if (!slots.length) return [];
    if (form.preferred_program === "OPEN_GYM") {
      const s = slots[0];
      const range = s?.start && s?.end ? `${fmt12hLead(s.start)} – ${fmt12hLead(s.end)}` : "";
      return [{ value: s?.label || "OPEN_GYM_FLEX", label: `Anytime (walk-in)${range ? ` · ${range}` : ""}` }];
    }
    const uniq = Array.from(new Set(slots.map((s) => s.start).filter(Boolean))).sort();
    return uniq.map((t) => ({ value: t, label: fmt12hLead(t) }));
  }, [slots, form.preferred_program]);

  const CORE_PROGRAMS = [
    { code: "STRENGTH", label: "Strength" },
    { code: "CONDITIONING", label: "Conditioning" },
    { code: "MMA", label: "MMA" },
    { code: "HYROX", label: "Hyrox" },
    { code: "OPEN_GYM", label: "Open Gym" },
  ];

  const applyPlan = (planId) => {
    setSelectedPlanId(planId);
    const p = plans.find((x) => x.id === planId);
    if (!p) return;
    setForm((x) => ({
      ...x, plan_name: p.name, category: p.category,
      price: String(p.price_inr || ""),
      duration_days: p.duration_days || 90,
      total_sessions: p.total_sessions || 0,
    }));
  };

  const convert = useMutation({
    mutationFn: async () => (await apiClient.post(`/leads/${lead.id}/convert-to-member`, {
      plan_id: selectedPlanId || null,
      plan_name: form.plan_name.trim() || null,
      category: form.category.trim() || null,
      price: form.price ? Number(form.price) : null,
      duration_days: Number(form.duration_days) || 90,
      total_sessions: Number(form.total_sessions) || 0,
      start_date: form.start_date,
      coach_id: lead.assigned_to || null,
      preferred_program: form.preferred_program || null,
      preferred_timeslot: form.preferred_timeslot || null,
    })).data,
    onSuccess: (d) => {
      toast.success(`Member created — ${d.membership.plan_name} · ${d.created_user ? "new account" : "linked to existing account"}`);
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["memberships"] });
      setOpen(false);
      onConverted && onConverted({ converted_membership_id: d.membership.id, converted_user_id: d.user_id });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Conversion failed"),
  });

  if (lead.converted_membership_id) {
    return (
      <Section label="Membership">
        <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-3 text-sm text-green-400" data-testid="member-created-badge">
          Member account created — ID: {lead.converted_user_id?.slice(0, 8)}…
        </div>
      </Section>
    );
  }

  return (
    <Section label="Create Membership">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button data-testid="open-convert-dialog"
            className="w-full bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-[0.14em] text-[11px] py-2.5 rounded-sm">
            Convert to Member →
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-[#0a0a0a] border-zinc-800 text-white max-w-lg" data-testid="convert-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase tracking-tight">Convert Lead to Member</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">Creates a customer account (if new) + active membership. Prefilled from the lead.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Pick a Plan (optional)</div>
              <Select value={selectedPlanId} onValueChange={applyPlan}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="convert-plan-select"><SelectValue placeholder="— pick a plan or fill custom fields below —" /></SelectTrigger>
                <SelectContent>{plans.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name} · ₹{p.price_inr} · {p.duration_days}d</SelectItem>
                ))}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Plan Name</div>
                <Input value={form.plan_name} onChange={(e) => setForm({ ...form, plan_name: e.target.value })} data-testid="convert-plan-name" className="bg-zinc-950 border-zinc-800" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Category</div>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="convert-category" className="bg-zinc-950 border-zinc-800" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Price (₹)</div>
                <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="convert-price" className="bg-zinc-950 border-zinc-800" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Duration (days)</div>
                <Input type="number" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} data-testid="convert-duration" className="bg-zinc-950 border-zinc-800" />
              </div>
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Start Date</div>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="convert-start-date" className="bg-zinc-950 border-zinc-800" />
              </div>
              <div className="col-span-2 border-t border-zinc-800 pt-3">
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2 font-semibold">Offering & Class Timing</div>
                <div className="text-[10px] text-zinc-600 mb-2">Prefilled from the lead. Change if the member is starting on a different program or timeslot.</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Offering (Program)</div>
                <Select value={form.preferred_program}
                  onValueChange={(v) => setForm((x) => ({ ...x, preferred_program: v, preferred_timeslot: "" }))}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="convert-preferred-program">
                    <SelectValue placeholder="— select a program —" />
                  </SelectTrigger>
                  <SelectContent>
                    {CORE_PROGRAMS.map((p) => (
                      <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Class Timing</div>
                <Select value={form.preferred_timeslot}
                  onValueChange={(v) => setForm((x) => ({ ...x, preferred_timeslot: v }))}
                  disabled={!form.preferred_program}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="convert-preferred-timeslot">
                    <SelectValue placeholder={form.preferred_program ? (timeOptions.length ? "— pick a time —" : "No times available") : "Pick a program first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {timeOptions.length === 0 ? (
                      <SelectItem value="__none__" disabled>No live schedule matches</SelectItem>
                    ) : timeOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="text-[10px] text-zinc-600 pt-1">Expiry auto-computed as Start Date + Duration.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={() => convert.mutate()} disabled={convert.isPending || !form.plan_name || !form.category || !form.price}
              data-testid="confirm-convert"
              className="bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-[0.14em]">
              {convert.isPending ? "Creating…" : "Create Member + Membership"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}

